import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Form, Field, FormElement, FieldRenderProps } from '@progress/kendo-react-form'

import {
  useCreateMailSettingsMutation,
  useLazyGetMailSettingsQuery,
  useCheckMailSettingsMutation,
  useUpdateMailSettingsMutation,
} from 'app/services/system.service'
import { BUTTONS, ERRORS, FORM, LDAP_MANAGEMENT, PLACEHOLDERS, TITLES } from 'locales/locales'
import { emailValidator, requiredValidator } from 'components/kendo/form/FormValidators'
import colors from 'utils/colors'

import CustomText from 'components/text/CustomText'
import CustomButton from 'components/button/CustomButton'
import Loading from 'components/spinner/Loading'
import { FormInput } from 'components/kendo/form/FormComponents'
import { useAppSelector } from 'app/hooks'
import { IRolePermissions, SettingsPermissions } from 'types/role.types'
import { isFlagSetIn } from 'utils/helpers/isSetInFlags'
import KendoSwitch from 'components/kendo/switch/Switch'
import DropdownAlert from 'components/dropdown-alert/DropdownAlert'
import { FixMeLater } from 'types'

interface MailSettings {
  hostname: string
  port: string
  from: string
  fromName: string
  username: string
  password: string
  ssl: boolean
}

const emptyMailSettings: MailSettings = {
  hostname: '',
  port: '',
  from: '',
  fromName: '',
  username: '',
  password: '',
  ssl: false,
}

const AdminMailProviderSettings: React.FC = () => {
  const { t } = useTranslation()
  const [mailDataId, setmailDataId] = useState('')
  const [mailTestSettings, setMailTestSettings] = useState<FixMeLater>({})
  const [savedMailSettings, setSavedMailSettings] = useState<MailSettings>(emptyMailSettings)
  const [initialFormData, setInitialFormData] = useState<MailSettings>(emptyMailSettings)
  const [getMailSettings] = useLazyGetMailSettingsQuery()
  const [createMailSettings] = useCreateMailSettingsMutation()
  const [updateMailSettings] = useUpdateMailSettingsMutation()
  const [checkMailSettings] = useCheckMailSettingsMutation()

  const permissions = useAppSelector<IRolePermissions>((state) => state.auth.permissions)

  const hasEmailUpdatePermission = isFlagSetIn(
    [SettingsPermissions.UPDATE_EMAIL_SETTINGS],
    permissions.settings,
  )

  useEffect(() => {
    fetchMailSettings()
  }, [])

  const fetchMailSettings = async () => {
    try {
      const data = await getMailSettings({})
      if (data.data.data[0]) {
        const mailData = data.data.data[0]
        setSavedMailSettings({ ...mailData })
        setInitialFormData({ ...emptyMailSettings, ssl: mailData.ssl ?? false })
        setMailTestSettings({ ...mailData })
        setmailDataId(mailData._id)
      }
    } catch (error) {
      console.log(error)
    }
  }

  const optionalRequiredValidator =
    (savedValue: string) =>
    (value: string): string =>
      mailDataId && !value?.trim() && savedValue ? '' : requiredValidator(value)

  const optionalEmailValidator =
    (savedValue: string) =>
    (value: string): string =>
      mailDataId && !value?.trim() && savedValue ? '' : emailValidator(value)

  const onSubmitHandler = async (values: { [name: string]: any }) => {
    const dataItem: MailSettings = {
      hostname: values.hostname || savedMailSettings.hostname,
      port: values.port || savedMailSettings.port,
      from: values.from || savedMailSettings.from,
      fromName: values.fromName || savedMailSettings.fromName,
      username: values.username || savedMailSettings.username,
      password: values.password || savedMailSettings.password,
      ssl: values.ssl,
    }
    try {
      Loading.ref.show()
      if (mailDataId === '') {
        await createMailSettings(dataItem)
      } else {
        await updateMailSettings({ ...dataItem, _id: mailDataId })
      }
    } finally {
      Loading.ref.hide()
    }
  }

  const testEmailSettings = async () => {
    try {
      Loading.ref.show()
      const data: FixMeLater = await checkMailSettings({
        hostname: mailTestSettings.hostname,
        port: mailTestSettings.port,
        from: mailTestSettings.from,
        fromName: mailTestSettings.fromName,
        username: mailTestSettings.username,
        password: mailTestSettings.password,
        ssl: mailTestSettings.ssl,
      })

      if (data?.data?.success) {
        return
      } 
    } catch (error) {
      DropdownAlert.ref.show({ isError: true, message: t(ERRORS.SOMETHING_WENT_WRONG_ERROR) })
    } finally {
      Loading.ref.hide()
    }
  }

  return (
    <ContentCard>
      <ContentCardHeader>{t(TITLES.MAIL_SETTINGS_TITLE)}</ContentCardHeader>
      <ContentCardBody>
        <Form
          initialValues={initialFormData}
          onSubmit={onSubmitHandler}
          key={mailDataId}
          render={({ allowSubmit }) => (
            <FormElement>
              <Layout>
                <ThreeColumnLayout>
                  <LeftColumn>
                    <Field
                      id="hostname"
                      name="hostname"
                      label={t(FORM.HOSTNAME)}
                      placeholder={savedMailSettings.hostname || t(PLACEHOLDERS.MAIL_HOST)}
                      onChange={(e) => {
                        setMailTestSettings({ ...mailTestSettings, hostname: e.target.value })
                      }}
                      validator={optionalRequiredValidator(savedMailSettings.hostname)}
                      component={FormInput}
                    />

                    <Field
                      id="from"
                      name="from"
                      label={t(FORM.FROM)}
                      placeholder={savedMailSettings.from || t(PLACEHOLDERS.MAIL_FROM)}
                      onChange={(e) => {
                        setMailTestSettings({ ...mailTestSettings, from: e.target.value })
                      }}
                      validator={optionalEmailValidator(savedMailSettings.from)}
                      component={FormInput}
                    />

                    <Field
                      id="username"
                      name="username"
                      label={t(FORM.EMAIL_API_USERNAME)}
                      placeholder={savedMailSettings.username || t(PLACEHOLDERS.MAIL_API_USERNAME)}
                      onChange={(e) => {
                        setMailTestSettings({ ...mailTestSettings, username: e.target.value })
                      }}
                      validator={optionalRequiredValidator(savedMailSettings.username)}
                      component={FormInput}
                    />

                    <SSLContainer>
                      <CustomText typography="header2" color={colors.black}>
                        SSL
                      </CustomText>
                      <Field
                        id="ssl"
                        name="ssl"
                        component={(fieldRenderProps: FieldRenderProps) => (
                          <KendoSwitch
                            checked={fieldRenderProps.value || false}
                            size={'small'}
                            onChange={(value: boolean) => {
                              fieldRenderProps.onChange({ target: { value } })
                              setMailTestSettings({ ...mailTestSettings, ssl: value })
                            }}
                          />
                        )}
                      />
                    </SSLContainer>
                  </LeftColumn>

                  <RightColumn>
                    <Field
                      id="port"
                      name="port"
                      label={t(FORM.PORT)}
                      placeholder={savedMailSettings.port || t(PLACEHOLDERS.MAIL_PORT)}
                      onChange={(e) => {
                        setMailTestSettings({ ...mailTestSettings, port: e.target.value })
                      }}
                      validator={optionalRequiredValidator(savedMailSettings.port)}
                      component={FormInput}
                    />

                    <Field
                      id="fromName"
                      name="fromName"
                      label={t(FORM.FROM_NAME)}
                      placeholder={savedMailSettings.fromName || t(PLACEHOLDERS.MAIL_FROM_NAME)}
                      onChange={(e) => {
                        setMailTestSettings({ ...mailTestSettings, fromName: e.target.value })
                      }}
                      validator={optionalRequiredValidator(savedMailSettings.fromName)}
                      component={FormInput}
                    />

                    <Field
                      id="password"
                      name="password"
                      label={t(FORM.PASSWORD)}
                      placeholder={savedMailSettings.password || t(PLACEHOLDERS.PASSWORD)}
                      type="password"
                      onChange={(e) => {
                        setMailTestSettings({ ...mailTestSettings, password: e.target.value })
                      }}
                      validator={optionalRequiredValidator(savedMailSettings.password)}
                      component={FormInput}
                    />

                    <ButtonWrapper>
                      <TestButton
                        text={t(LDAP_MANAGEMENT.TEST_CONNECTION)}
                        type={'secondary'}
                        buttonType={'button'}
                        onClick={testEmailSettings}
                      />
                      {hasEmailUpdatePermission ? (
                        <SaveButton
                          text={t(BUTTONS.SAVE)}
                          type={'primary'}
                          disabled={!allowSubmit}
                        />
                      ) : null}
                    </ButtonWrapper>
                  </RightColumn>
                </ThreeColumnLayout>
              </Layout>
            </FormElement>
          )}
        />
      </ContentCardBody>
    </ContentCard>
  )
}

export default AdminMailProviderSettings

const ContentCard = styled.div`
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2), 0 0 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
  border: 1px solid #e3ebf6;
`

const ContentCardHeader = styled.div`
  padding: 16px 20px;
  font-size: 16px;
  font-weight: 500;
  border-bottom: 1px solid #e3ebf6;
`

const ContentCardBody = styled.div`
  padding: 20px;
`

const Layout = styled.div`
  padding: 20px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const ThreeColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  .k-input-solid {
    height: 35px !important;
    font-size: 15px !important;
  }
`

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  .k-input-solid {
    height: 35px !important;
    font-size: 15px !important;
  }
`

const SSLContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 24px;
  margin-top: 50px;
`

const TestButton = styled(CustomButton)`
  cursor: pointer !important;
`

const SaveButton = styled(CustomButton)`
  cursor: pointer !important;
  background-color: #1f4ab7;
  &:hover {
    background-color: #3f63be;
  }
`
